import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Typography } from 'components/common'
import {
  CategoryThreadsQueryResult,
  CollectiveProposalType,
  useCommentAddedSubscription,
  useThreadChangedSubscription
} from 'graphql/generated'
import ProposalListItem from '../ProposalsTable/ProposalListItem'
import { useCollectiveContext } from 'context/CollectiveContext'
import { twMerge } from 'tailwind-merge'
import {
  faChevronUp,
  faChevronDown,
  faEye
} from '@fortawesome/pro-solid-svg-icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import BaseThreadHeader from './BaseThreadHeader'
import { inflect } from 'inflection'
import CollaboratorsModal from './CollaboratorsModal'
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons'
import SurveyListItem from '../ProposalsTable/SurveyListItem'
import useConvertS3KeysToImageUrl from './useConvertS3KeysToImageUrls'
import { useRouter } from 'next/router'
import QuillContent from 'components/QuillContent'
import ThreadProposal from './ThreadProposal'

export type TThreadListItem = {
  thread: CategoryThreadsQueryResult['category']['threads']['edges'][0]['node']
}

const heightLimitCollapsed = 100

const ThreadListItem: React.FC<TThreadListItem> = ({ thread }) => {
  const router = useRouter()
  const { collective } = useCollectiveContext()

  const elementRef = useRef<HTMLDivElement>(null)

  const [enableDisplayMoreButton, setEnableDisplayMoreButton] =
    useState<boolean>(false)
  const [displayingMore, setDisplayingMore] = useState<boolean>(false)

  const updateUrls = useConvertS3KeysToImageUrl(collective.discussionBoard.id)

  const [body, setBody] = useState<string>()

  useThreadChangedSubscription({
    variables: {
      threadId: thread.id
    }
  })
  useCommentAddedSubscription({
    variables: {
      threadId: thread.id
    }
  })

  useEffect(() => {
    async function parseBodyUrl() {
      if (thread) {
        setBody(await updateUrls(thread.body))
      }
    }
    parseBodyUrl()
  }, [thread, updateUrls])

  const renderedBody = useMemo(
    () => <QuillContent content={body} ref={elementRef} />,
    [body]
  )

  useEffect(() => {
    const enableButton =
      (elementRef.current?.clientHeight || 0) > heightLimitCollapsed
        ? true
        : false
    setEnableDisplayMoreButton(enableButton)
    setDisplayingMore(enableButton ? false : true)
  }, [renderedBody])

  return (
    <div
      className={twMerge(
        'px-4 py-4 mb-4 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer',
        thread.isPinned && 'bg-orange-50'
      )}
      onClick={() => router.push(thread.publicUrl)}
    >
      <div key={thread.id} className="space-y-4">
        <BaseThreadHeader thread={thread} />

        {!thread.proposal && thread.body !== '' && (
          <div
            className={twMerge(
              'flex-1',
              displayingMore ? 'max-h-full' : 'max-h-24 overflow-hidden'
            )}
          >
            {renderedBody}
          </div>
        )}

        {thread.proposal?.type === CollectiveProposalType.COMMANDS && (
          <>
            <ThreadProposal thread={thread} proposal={thread.proposal} />
            <ProposalListItem
              proposal={{ ...thread.proposal, collective }}
              threadView
            />
          </>
        )}

        {thread.proposal?.type === CollectiveProposalType.SURVEY && (
          <>
            <ThreadProposal thread={thread} proposal={thread.proposal} />
            <SurveyListItem
              proposal={{ ...thread.proposal, collective }}
              threadView
            />
          </>
        )}

        <div className="flex justify-between items-center">
          <div className="flex space-x-3">
            <Typography color="blue" size="sm">
              {`${thread.replyCount} Comments`}
            </Typography>

            {!!thread.attachments.length && (
              <Typography color="lightGray" size="sm">
                <FontAwesomeIcon icon={faPaperclip} size="sm" />
                {` ${thread.attachments.length} ${inflect(
                  `Attachments`,
                  thread.attachments.length
                )}`}
              </Typography>
            )}

            <Typography color="lightGray" size="sm">
              <FontAwesomeIcon icon={faEye} size="sm" />
              {` Viewed ${thread.viewsCount} ${inflect(
                `times`,
                thread.viewsCount
              )}`}
            </Typography>
          </div>

          <div className="flex">
            {enableDisplayMoreButton && (
              <Button
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  setDisplayingMore(!displayingMore)
                }}
                color="textOnly"
                size="sm"
                showFocusRing={false}
              >
                <span className="hidden md:inline-block">
                  {displayingMore ? `Show Less` : `Show More`}
                </span>
                <FontAwesomeIcon
                  icon={displayingMore ? faChevronUp : faChevronDown}
                  className="h-3 w-3 ml-2"
                />
              </Button>
            )}

            <CollaboratorsModal thread={thread} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ThreadListItem
